import React, { useEffect, useRef } from "react";
import { DataGrid } from "@mui/x-data-grid";
import { useTheme } from "@mui/material/styles";
import { Box } from "@mui/material";

const DataGridFull = ({
  is_folders = false,
  selectable = true,
  rows,
  columns,
  searchValue = "",
  onClick,
  initial_sort_field = "id",
  initial_asc = "asc",
  handleSortChange = () => {}, // Add the sorting change handler here
  handleSelectionModelChange = () => {}, // Add the selection model change handler here
  initial_saved_columns_sizes = {},
  initial_saved_columns_showed = is_folders
    ? {
        id: false,
        creation_date: false,
        state: false,
      }
    : { id: false }, // Set the default initial column visibility
  initialPage = 0, // Set the default initial page
  initialPageSize = 100, // Set the default initial page size
  setPaginationModel = () => {},
  handleColumnManagementChange = () => {},
  handleColumnSizeChange = () => {},
  rowHeight = 40,
}) => {
  const theme = useTheme();

  useEffect(() => {
    // Set the initial column visibility
    handleSetColumnVisibility(searchValue, initial_saved_columns_showed);
  }, [searchValue]);

  const [sort_field, setSortField] = React.useState(initial_sort_field);
  const [column_visibility_model, setColumnVisibilityModel] = React.useState(
    is_folders
      ? {
          ...initial_saved_columns_showed,
          state: searchValue === "" ? false : true,
        }
      : initial_saved_columns_showed
  );

  const [asc, setAsc] = React.useState(initial_asc);
  const [initialised, setInitialised] = React.useState(false);
  const handleRowClick = (params) => {
    onClick(params.row);

    //onClick(params.row.id);
    // Perform your custom action here, e.g., navigating to another page or displaying more details
  };

  const handleSetColumnVisibility = (newsearch = null, new_model) => {
    let search_value = newsearch ? newsearch : searchValue;
    // if searchValue != "" add the status column as visible, else it must be not visible
    let new_column_visibility_model = new_model
      ? { ...new_model }
      : { ...column_visibility_model };
    if (is_folders) {
      if (search_value !== "") {
        // look into column_visibility_model and set the status column to true
        new_column_visibility_model["state"] = true;
      } else {
        // look into column_visibility_model and set the status column to false
        new_column_visibility_model["state"] = false;
      }
    }

    setColumnVisibilityModel(new_column_visibility_model);
    handleColumnManagementChange(new_column_visibility_model);
  };

  const handleSortModelChange = (sortModel) => {
    if (!initialised) {
      setInitialised(true);
      setSortField(initial_sort_field);
      setAsc(initial_asc);
      return;
    }
    let new_sort_field = null;
    let new_asc = null;
    if (sortModel && sortModel[0]) {
      new_sort_field = sortModel[0].field;
      new_asc = sortModel[0].sort;
    }
    setSortField(new_sort_field);
    setAsc(new_asc);

    if (handleSortChange) {
      handleSortChange(
        new_sort_field && new_asc
          ? [{ field: new_sort_field, sort: new_asc }]
          : []
      );
    }
  };
  const handlePaginationModelChange = (model) => {
    setPaginationModel(model);
  };

  const frenchLocaleText = {
    // General text
    noRowsLabel: "Pas de ligne",
    noResultsOverlayLabel: "Aucun résultat trouvé.",

    // Toolbar
    toolbarDensity: "Densité",
    toolbarDensityLabel: "Densité",
    toolbarDensityCompact: "Compact",
    toolbarDensityStandard: "Standard",
    toolbarDensityComfortable: "Confortable",

    // Columns panel
    columnsPanelTextFieldLabel: "Trouver une colonne",
    columnsPanelTextFieldPlaceholder: "Titre de colonne",
    columnsPanelDragIconLabel: "Réorganiser les colonnes",
    columnsPanelShowAllButton: "Afficher tout",
    columnsPanelHideAllButton: "Masquer tout",

    // Filter panel
    filterPanelAddFilter: "Ajouter un filtre",
    filterPanelDeleteIconLabel: "Supprimer",
    filterPanelLinkOperator: "Opérateur logique",
    filterPanelOperators: "Opérateurs",

    // Filter operators
    filterOperatorContains: "contient",
    filterOperatorEquals: "égal à",
    filterOperatorStartsWith: "commence par",
    filterOperatorEndsWith: "se termine par",
    filterOperatorIs: "est",
    filterOperatorNot: "n'est pas",
    filterOperatorAfter: "est après",
    filterOperatorOnOrAfter: "est le ou après",
    filterOperatorBefore: "est avant",
    filterOperatorOnOrBefore: "est le ou avant",
    filterOperatorIsEmpty: "est vide",
    filterOperatorIsNotEmpty: "n'est pas vide",

    // Column menu
    columnMenuLabel: "Menu",
    columnMenuShowHideAll: "Afficher les colonnes",
    columnMenuManageColumns: "Gérer les colonnes",
    columnMenuFilter: "Filtrer",
    columnMenuHideColumn: "Masquer",
    columnMenuUnsort: "Annuler le tri",
    columnMenuSortAsc: "Trier par ordre croissant",
    columnMenuSortDesc: "Trier par ordre décroissant",
  };
  const [rowSelectionModel, setRowSelectionModel] = React.useState([]);

  const handleRowSelectionModelChange = (newSelection) => {
    handleSelectionModelChange(newSelection);
    setRowSelectionModel(newSelection);
  };

  function columnsWidthConfig(columns) {
    // use initial_saved_columns_sizes to set the width of the columns

    columns = columns.filter((column) => column !== null);
    for (let i = 0; i < columns.length; i++) {
      if (columns[i] && initial_saved_columns_sizes[columns[i]?.field]) {
        columns[i].width = initial_saved_columns_sizes[columns[i]?.field];
      }
    }

    return columns;
  }

  return (
    <Box
      className="bg-white border-shadow_10 border-1 rounded-xl"
      sx={{
        height: "100%", // Adjusted height for better appearance
        width: "100%",
        minWidth: "100%",
        minHeight: "200px",

        backgroundColor: "white", // White background for the grid
        boxShadow: "0px 4px 12px rgba(0, 0, 0, 0)", // Add shadow for elegance
        borderRadius: "6px", // Rounded corners
        pointerEvents: "auto", // Ensure the grid is interactive
      }}
    >
      <div
        className=""
        style={{
          height: "100%", // Adjusted height for better appearance
          minHeight: "100px",
          pointerEvents: "auto", // Ensure the grid is interactive
        }}
      >
        <DataGrid
          sx={{
            "& .MuiDataGrid-footerContainer": {
              flexDirection: "row-reverse", // This reverses the order of elements
              justifyContent: "flex-start", // Aligns content to the left
              pointerEvents: "auto", // Ensure the footer is interactive
            },
            "& .MuiTablePagination-root": {
              marginLeft: 0, // Removes default left margin
              marginRight: "auto", // Pushes other elements to the right
              pointerEvents: "auto", // Ensure the footer is interactive
            },
            // modify the checkbox style
            "& .MuiCheckbox-root": {
              color: "lightgrey",
            },
            "& .MuiDataGrid-menu": {
              pointerEvents: "auto", // Ensure the footer is interactive
              zIndex: 9999, // Ensure the menu is on top
            },

            "& .MuiDataGrid-root": {
              border: "none", // Remove border around the grid
              pointerEvents: "auto", // Ensure the footer is interactive
            },
            "& .MuiDataGrid-cell": {
              borderTop: `1px solid ${theme.palette.shadow._2}`, // Add a border above the cells
              borderBottom: `1px solid ${theme.palette.shadow._2}`, // Add a border under the column headers
              color: "black", // Text color for cells
              fontSize: "13px", // Text size for cells
              margin: "0px", // Margin inside cells
              pointerEvents: "auto", // Ensure the footer is interactive
            },
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: theme.palette.primary.light, // Background color for column headers
              color: theme.palette.primary.contrastText, // Text color for column headers
              fontSize: "14px", // Text size for column headers
              border: "none",
              pointerEvents: "auto", // Ensure the footer is interactive

              minWidth: "100%",
              width: "100%",
            },
            "& .MuiDataGrid-columnHeaderTitle": {
              fontWeight: "medium", // Make the column headers bold
              fontSize: "14px", // Text size for column header titles
              color: theme.palette.shadow._50, // Text color for column header titles
              border: "none",
            },

            "& .MuiDataGrid-row:hover": {
              backgroundColor: theme.palette.action.hover, // Background
              pointerEvents: "auto", // Ensure the footer is interactive

              cursor: "pointer", // Change cursor to pointer on hover
              //          color for row on hover
            },
          }}
          rows={rows}
          columns={columnsWidthConfig(columns)}
          getRowId={(row) => row.id}
          pageSize={50}
          rowsPerPageOptions={[10, 20]}
          disableSelectionOnClick
          rowHeight={rowHeight} // Custom row height
          columnHeaderHeight={30}
          autoPageSize={false}
          pageSizeOptions={[50]}
          localeText={frenchLocaleText} // Apply French localization
          onRowClick={handleRowClick} // Trigger the action when a row is clicked
          onRowSelectionModelChange={(newRowSelectionModel) => {
            handleRowSelectionModelChange(newRowSelectionModel);
          }}
          rowSelectionModel={rowSelectionModel}
          sortModel={[
            sort_field && asc ? { field: sort_field, sort: asc } : {},
          ]}
          onSortModelChange={handleSortModelChange} // Add the sorting change handler here
          pagination={true}
          onPaginationModelChange={handlePaginationModelChange}
          onColumnVisibilityModelChange={(newVisibilityModel) => {
            handleSetColumnVisibility(searchValue, newVisibilityModel);
          }}
          columnVisibilityModel={column_visibility_model}
          onColumnResize={(params) => {
            handleColumnSizeChange(params);
          }}
          checkboxSelection={selectable}
          initialState={{
            pagination: {
              paginationModel: {
                pageSize: 50,
                page: initialPage,
              },
            },

            columns: {
              columnVisibilityModel: initial_saved_columns_showed,
            },
          }}
        />
      </div>
    </Box>
  );
};

export default DataGridFull;
